import {
  AuthError,
  ConflictError,
  NotFoundError,
  PermissionError,
  RateLimitError,
  UserBlockingError,
  ValidationError,
} from '@hookmyapp/observability/errors';
import { parseApiError } from '@/lib/api-error';

function errorCode(body: unknown, status: number): string {
  if (
    typeof body === 'object' &&
    body !== null &&
    'code' in body &&
    typeof (body as { code?: unknown }).code === 'string' &&
    (body as { code: string }).code.length > 0
  ) {
    return (body as { code: string }).code;
  }

  return `HTTP_${status}`;
}

function requestId(body: unknown): string | undefined {
  if (
    typeof body === 'object' &&
    body !== null &&
    'requestId' in body &&
    typeof (body as { requestId?: unknown }).requestId === 'string'
  ) {
    return (body as { requestId: string }).requestId;
  }

  return undefined;
}

export async function throwForResponse(
  res: Response,
  path: string,
  fallback = 'Something went wrong. Please try again.',
): Promise<never> {
  let body: unknown = null;

  try {
    body = await res.clone().json();
  } catch {
    body = null;
  }

  const normalized = parseApiError(res.status, body, fallback);
  const details = {
    status: res.status,
    path,
    requestId: requestId(body),
  };
  const args = {
    message: `API ${res.status} ${path}`,
    code: errorCode(body, res.status),
    details,
    userMessage: normalized.userMessage,
  };

  switch (res.status) {
    case 400:
      throw new ValidationError(args);
    case 401:
      throw new AuthError(args);
    case 403:
      throw new PermissionError(args);
    case 404:
      throw new NotFoundError(args);
    case 409:
      throw new ConflictError(args);
    case 429:
      throw new RateLimitError(args);
    default:
      if (res.status >= 500) {
        throw new UserBlockingError(args);
      }
      throw new ValidationError(args);
  }
}
